import { useCallback, useEffect, useState } from "react";
import {
  fetchFoodRetailFreshnessSummary,
  fetchFoodRetailIngestionRuns,
} from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatDateTime } from "./foodRetailIngestionHelpers";

const FoodAraFreshnessSection = ({ sourceKey }) => {
  const [freshness, setFreshness] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const load = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setFreshness(null);
      setRuns([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const [freshnessPayload, runsPayload] = await Promise.all([
        fetchFoodRetailFreshnessSummary({
          sourceKey,
          freshnessWindowHours: 72,
        }),
        fetchFoodRetailIngestionRuns({
          sourceKey,
          limit: 5,
        }),
      ]);
      setFreshness(freshnessPayload);
      setRuns(runsPayload.runs || []);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load freshness summary.");
      setFreshness(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = freshness?.summary;
  const staleItems = (freshness?.basketItems || []).filter(
    (item) => item.freshnessStatus === "STALE"
  );
  const missingItems = (freshness?.basketItems || []).filter(
    (item) => item.freshnessStatus === "NO_DATA"
  );

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Freshness &amp; Re-Run Health</h2>
          <p className="muted">
            Fresh vs stale observations for the Basic Basket window (72h). Same-run duplicates are
            deduped; cross-run history is preserved.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}

      {summary ? (
        <div className="food-retail-ingestion-page__kpi-row">
          <div>
            <span className="muted">Latest observation</span>
            <strong>{formatDateTime(summary.latestObservationAt) || "—"}</strong>
          </div>
          <div>
            <span className="muted">Fresh</span>
            <strong>{summary.freshObservationCount}</strong>
          </div>
          <div>
            <span className="muted">Stale</span>
            <strong>{summary.staleObservationCount}</strong>
          </div>
          <div>
            <span className="muted">Basket staples fresh</span>
            <strong>{summary.basketStaplesWithFreshObservation ?? "—"}</strong>
          </div>
          <div>
            <span className="muted">Stale-only staples</span>
            <strong>{summary.basketStaplesStaleOnly ?? "—"}</strong>
          </div>
          <div>
            <span className="muted">Status</span>
            <strong>{summary.status}</strong>
          </div>
        </div>
      ) : null}

      {runs.length ? (
        <>
          <h3>Recent ingestion runs</h3>
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Inserted</th>
                  <th>Deduped</th>
                  <th>Skipped</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id || run.runId}>
                    <td>{formatDateTime(run.startedAt)}</td>
                    <td>{run.status}</td>
                    <td>{run.observationsInserted ?? "—"}</td>
                    <td>{run.resultJson?.dedupe?.observationsDeduped ?? "—"}</td>
                    <td>{run.observationsSkipped ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}

      {staleItems.length ? (
        <>
          <h3>Stale-only staples</h3>
          <ul className="muted">
            {staleItems.map((item) => (
              <li key={item.productId}>
                {item.canonicalProductName} — last seen {formatDateTime(item.latestCapturedAt)}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {missingItems.length ? (
        <>
          <h3>Missing staples</h3>
          <ul className="muted">
            {missingItems.map((item) => (
              <li key={item.productId}>{item.canonicalProductName}</li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
};

export default FoodAraFreshnessSection;
